import { ADMIN_FIGURES, SHIPPING_FIGURES_KEY, SHIPPING_GROUPS, showsAdminFigures, withWidget, type ShippingGroup } from '@/domain';
import { SHIPPING_TAB_LABEL } from './shippingTabs';
import { useTableLayout } from '@/application/usecases/useTableLayout';
import { strings } from '@/shared/strings';
import { KpiCard, KpiGrid } from '@/ui/data/KpiCard';
import { Button } from '@/ui/primitives/Button';
import { toast } from '@/ui/primitives/toastStore';
import styles from './ShippingFigures.module.css';

/**
 * The shipping desk's figures: how many cases stand in each tab, a card each,
 * in the tabs' own order, and each opens its tab. The lab's request of
 * 22 October 2026.
 *
 * Counted over every case the screen holds, whatever the search narrows the
 * list to, so a figure is the size of its tab before anything is typed. On the
 * screen until the reader takes them off, and put back from the same place,
 * kept on their account: what sits on a person's screen is theirs to remove.
 */
export function ShippingFigures({
  counts,
  current,
  onSelect,
}: {
  /** Each tab's count; undefined while loading, null when it could not be read. */
  counts: Record<ShippingGroup, number | null | undefined>;
  current: ShippingGroup;
  onSelect: (tab: ShippingGroup) => void;
}) {
  const layout = useTableLayout(SHIPPING_FIGURES_KEY);
  const shown = showsAdminFigures(layout.layout);

  function show(on: boolean) {
    layout.save(withWidget(ADMIN_FIGURES, layout.layout, 'figures', on));
    if (!on) toast(strings.adminFigures.takenOff);
  }

  if (!shown) {
    return (
      <p className={styles.off}>
        <Button variant="text" onClick={() => show(true)}>
          {strings.adminFigures.showAgain}
        </Button>
      </p>
    );
  }

  return (
    <div className={styles.figures}>
      <KpiGrid label={strings.shipping.figuresLabel}>
        {SHIPPING_GROUPS.map((tab) => (
          <KpiCard
            key={tab}
            label={SHIPPING_TAB_LABEL[tab]}
            value={counts[tab]}
            pressed={current === tab}
            onPress={() => onSelect(tab)}
          />
        ))}
      </KpiGrid>
      <div className={styles.foot}>
        <Button variant="text" onClick={() => show(false)}>
          {strings.adminFigures.hide}
        </Button>
      </div>
    </div>
  );
}
